import usePrivateAxios from "@/common/api/usePrivateAxios";
import { transformNewGeos } from "@/common/utils/geoTransformers";
import { getCleanPayload } from "@/common/utils/getCleanPayload";
import { getDirtyRailsUpdate } from "@/common/utils/getDirtyRailsUpdate";
import { mapToDeletion } from "@/common/utils/railsHelpers";
import { unFormatCnpj } from "@/common/utils/unFormatters";
import { PrivateCompanyLeadsFunnelUpdateContractValidationSchema } from "@/features/privateCompanyLeadsFunnel/schemas/update.schema";
import { PrivateCompanyLeadData } from "@/features/privateCompanyLeadsFunnel/types/privateCompanyLead";
import { useMutation } from "@tanstack/react-query";

interface UseUpdatePrivateCompanyLeadContract {
  privateCompanyLead?: PrivateCompanyLeadData,
  onSuccess: () => void,
  onError: (error: any) => void
}

const useUpdatePrivateCompanyLeadContract = ({ privateCompanyLead, onSuccess, onError }: UseUpdatePrivateCompanyLeadContract) => {
  const privateAxios = usePrivateAxios();
  return useMutation({
    mutationKey: ['updateContract'],
    mutationFn: async (data: Partial<PrivateCompanyLeadsFunnelUpdateContractValidationSchema>) => {
      const payload = {
        contract_attributes: {
          ...data.contract,
          duration_in_months: data.contract?.duration_in_months?.value || undefined,
          bill_grouping_mode: data.contract?.bill_grouping_mode?.value || undefined,
          bill_by: data.contract?.bill_by?.value || undefined,
          cycle: data.contract?.cycle?.value || undefined,
          cycle_start_day: data.contract?.cycle_start_day?.value ?? undefined,
          cycle_fixed_interval: data.contract?.cycle_fixed_interval || undefined,
          closing_to_due_in_days: data.contract?.closing_to_due_in_days?.value || undefined,
          due_to_suspension_in_days: data.contract?.due_to_suspension_in_days?.value || undefined,
          cnpj_for_fiscal_document: data.contract?.cnpj_for_fiscal_document?.value
            ? unFormatCnpj(data.contract?.cnpj_for_fiscal_document.value)
            : undefined,
          payment_method: data.contract?.payment_method?.value || undefined,
          fiscal_document_requirement: data.contract?.fiscal_document_requirement?.value || undefined,
          contract_equipment_categories_attributes: data.contract?.contract_equipment_categories,
          contract_product_categories_attributes: getDirtyRailsUpdate(
            privateCompanyLead?.contract?.contract_product_categories || [],
            data.contract?.contract_product_categories || undefined,
            ({ product_category }) => product_category.id,
            ({ value }) => value,
            mapToDeletion,
            ({ value }) => ({ product_category_id: value }),
          ),
          geo_geoables_attributes: getDirtyRailsUpdate(
            privateCompanyLead?.contract?.geo_geoables || [],
            data?.contract?.geo_geoables || undefined,
            (geo) => geo.geo.id,
            (geo) => geo.value || '',
            mapToDeletion,
            transformNewGeos,
          ),
        }
      }

      delete payload.contract_attributes.contract_product_categories;
      delete payload.contract_attributes.geo_geoables;

      await privateAxios.put(`/funnel/private_company_leads/${privateCompanyLead?.id}`, {
        private_company_lead: getCleanPayload(payload)
      })
    },
    onSuccess,
    onError,
  });
}

export default useUpdatePrivateCompanyLeadContract
